(() => {
  'use strict';
  if (window.tnmHalloweenGiftGuard) return;
  const element = document.getElementById('tnm-halloween-gift-guard-config');
  const notice = document.getElementById('tnm-halloween-gift-guard-notice');
  if (!element || !notice) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch { config = {}; }
  const positiveId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const offerRows = Array.isArray(config.offers) ? config.offers : [];
  const offers = offerRows.filter((offer) =>
    positiveId(offer?.gift) && Array.isArray(offer.bundles) && offer.bundles.length && offer.bundles.every(positiveId) &&
    (offer.campaigns === undefined || (Array.isArray(offer.campaigns) && offer.campaigns.length && offer.campaigns.every((value) => typeof value === 'string' && value.length)))
  );
  const gifts = new Set(offers.map((offer) => Number(offer.gift)));
  const bundles = new Set(offers.flatMap((offer) => offer.bundles.map(Number)));
  const configured = offers.length >= 1 && offers.length <= 4 && typeof config.currency === 'string' && config.currency.length === 3 &&
    [...gifts].every((gift) => !bundles.has(gift));
  const route = typeof config.root === 'string' && /^\/(?:[^?#]*\/)?$/.test(config.root) ? config.root : '/';
  const messages = config.messages || {};
  let inFlight = null;
  let inFlightSeed = null;
  let checkoutBusy = false;
  let replayForm = null;
  let replayButton = null;
  let timer = null;
  let lastRun = 0;
  const campaignMatches = (offer, item) => (offer.campaigns || [offer.campaign || 'halloween-2026']).includes(item.properties?._tnm_campaign);
  const knownCampaigns = new Set(['halloween-2026', 'mediterranean-199-20261005', 'halloween-weeknight-219-20261005']);
  const unresolvedCampaignLine = (item) => knownCampaigns.has(item.properties?._tnm_campaign) &&
    !offers.some((offer) => campaignMatches(offer, item) &&
      (Number(offer.gift) === Number(item.variant_id) || offer.bundles.map(Number).includes(Number(item.variant_id))));
  const owner = (item) => offers.find((offer) => Number(offer.gift) === Number(item.variant_id) && campaignMatches(offer, item));
  const owns = (item) => Boolean(owner(item));
  const signature = (item) => JSON.stringify([
    Number(item.variant_id), item.selling_plan_allocation?.selling_plan?.id || null,
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
  ]);
  const inventory = (items) => {
    const quantities = new Map();
    for (const item of items) quantities.set(signature(item), (quantities.get(signature(item)) || 0) + item.quantity);
    return JSON.stringify([...quantities].filter(([, quantity]) => quantity).sort(([a], [b]) => a.localeCompare(b)));
  };
  const show = (reason, focus = false) => {
    const text = reason === 'removed_gift' ? messages.removed : reason === 'adjusted_gift' ? messages.adjusted : messages.error;
    notice.querySelector('[data-hw-guard-message]').textContent = text || messages.cart || 'Please review your cart before checking out.';
    notice.hidden = false;
    if (focus) notice.focus({ preventScroll: true });
  };
  const request = async (path, payload) => {
    const response = await window.fetch(route + path, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    if (!response?.ok) throw new Error('unavailable');
    const value = await response.json();
    if (!value || value.errors || value.status >= 400) throw new Error('unavailable');
    return value;
  };
  const checkCart = (cart) => {
    if (!Array.isArray(cart.items) || cart.currency !== config.currency || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) throw new Error('unreadable');
    for (const item of cart.items) {
      if (!item || !positiveId(item.variant_id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
        (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties)))) throw new Error('unreadable');
      if (owns(item) && (typeof item.key !== 'string' || !item.key || item.gift_card !== true || item.selling_plan_allocation ||
        item.original_price !== (owner(item).giftValue || 7500) || !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0)) throw new Error('unreadable');
    }
    // Liquid may render empty rows for retired products. Keep active offers usable,
    // but never certify a campaign line whose own offer could not be resolved.
    if (cart.items.some(unresolvedCampaignLine)) throw new Error('unconfigured');
    if (cart.items.reduce((sum, item) => sum + item.quantity, 0) !== cart.item_count) throw new Error('unreadable');
    return cart;
  };
  const readCart = async () => checkCart(await request('cart.js'));
  // A seed is a full cart body the builder received from its own last cart
  // write; it passes the same checks as a read or the cart is read instead.
  const startCart = async (seed) => {
    if (seed) { try { return checkCart(seed); } catch { /* Read below. */ } }
    return readCart();
  };
  const step = (cart) => {
    const owned = cart.items.filter(owns);
    const eligible = (gift) => offers.some((offer) => Number(offer.gift) === Number(gift.variant_id) && campaignMatches(offer, gift) &&
      cart.items.some((item) => offer.bundles.map(Number).includes(Number(item.variant_id))));
    const unsafe = owned.find((gift) => !eligible(gift) || gift.final_line_price !== 0);
    if (unsafe) return { item: unsafe, quantity: 0 };
    const seen = new Set();
    for (const gift of owned) {
      const identity = Number(gift.variant_id);
      if (seen.has(identity)) return { item: gift, quantity: 0 };
      seen.add(identity);
      if (gift.quantity > 1) return { item: gift, quantity: 1 };
    }
    return null;
  };
  const reconcile = async (seed) => {
    let cart = null;
    let changed = '';
    try {
      cart = await startCart(seed);
      if (!configured) {
        if (cart.items.some((item) => ['halloween-2026', 'mediterranean-199-20261005', 'halloween-weeknight-219-20261005'].includes(item.properties?._tnm_campaign))) throw new Error('unconfigured');
        if (window.tnmCaseShipping) cart = await window.tnmCaseShipping.ensure(cart);
        return { safe: true, cart, reason: 'not_applicable' };
      }
      for (let writes = 0; writes < 5; writes += 1) {
        const change = step(cart);
        if (!change) {
          if (window.tnmCaseShipping) cart = await window.tnmCaseShipping.ensure(cart);
          return { safe: !changed, cart, reason: changed || 'safe' };
        }
        const expected = inventory(cart.items.map((item) => item === change.item ? { ...item, quantity: change.quantity } : item));
        let writeFailed = false;
        try { await request('cart/change.js', { id: change.item.key, quantity: change.quantity }); } catch { writeFailed = true; }
        cart = await readCart();
        if (inventory(cart.items) !== expected) return { safe: false, cart, reason: 'persistence_failed' };
        changed = change.quantity === 0 ? 'removed_gift' : changed || 'adjusted_gift';
        if (writeFailed) return { safe: false, cart, reason: changed };
      }
      return { safe: false, cart, reason: 'review_required' };
    } catch {
      return { safe: false, cart, reason: 'unavailable' };
    }
  };
  const ensureSafe = (seed) => {
    if (!inFlight) {
      lastRun = Date.now();
      inFlightSeed = seed || null;
      inFlight = reconcile(seed).then((result) => {
        // Background re-checks must not cover the checkout CTA over a transient
        // cart read failure; the checkout click path (intercept) still blocks.
        if (!result.safe && result.reason !== 'unavailable') show(result.reason);
        return result;
      }).finally(() => { inFlight = null; inFlightSeed = null; });
    }
    return inFlight;
  };
  // The builder's checkout handshake. `seed` is the cart its final write
  // returned. A run this same handshake already started from that seed is
  // shared; a run started before the handshake may predate the builder's
  // write, so it is awaited and the cart is read fresh.
  const ensureForHandshake = async (seed) => {
    if (inFlight && seed && inFlightSeed === seed) return inFlight;
    if (inFlight) { await inFlight; return ensureSafe(); }
    return ensureSafe(seed);
  };
  window.tnmHalloweenGiftGuard = { ensureSafe, ensureForHandshake };
  const dismiss = notice.querySelector('[data-hw-guard-dismiss]');
  if (dismiss) dismiss.addEventListener('click', () => { notice.hidden = true; });
  const landingBusy = () => !!document.querySelector('[data-hw-buy][aria-busy="true"], [data-med-buy][aria-busy="true"], [data-ts-cart-mutating="true"]');
  const recheck = () => {
    if (timer) return;
    timer = window.setTimeout(() => {
      timer = null;
      if (landingBusy() || checkoutBusy) { recheck(); return; }
      ensureSafe();
    }, Math.max(900, 3000 - (Date.now() - lastRun)));
  };
  const watchForeignRemoval = (button) => {
    const Observer = document.defaultView?.MutationObserver;
    if (!Observer) { recheck(); return; }
    const observer = new Observer(() => {
      // The native builder removes the row only after Shopify accepts its cart write.
      if (button.isConnected) return;
      observer.disconnect();
      ensureSafe().then((result) => {
        // The builder does not observe cart writes made by this guard. Reload only
        // after a confirmed gift removal so its displayed total matches Shopify.
        if (result.reason === 'removed_gift' && result.cart && !result.cart.items.some(owns)) {
          window.location.reload();
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  };
  const checkoutPath = (value) => {
    try {
      const url = new URL(value, window.location.href);
      return url.origin === window.location.origin && /\/checkouts?(?:\/|$)/.test(url.pathname);
    } catch { return false; }
  };
  const cartForm = (form) => {
    try {
      const url = new URL(form.action, window.location.href);
      return url.origin === window.location.origin && /\/cart\/?$/.test(url.pathname);
    } catch { return false; }
  };
  const intercept = async (event, resume) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      const result = await ensureSafe();
      if (result.safe) { notice.hidden = true; resume(); }
      else show(result.reason, true);
    } catch { show('unavailable', true); }
    finally { checkoutBusy = false; }
  };
  const resumeForm = (form, submitter) => {
    replayForm = form;
    try { form.requestSubmit(submitter || undefined); }
    finally { replayForm = null; }
  };
  // These native builder controls only navigate; normal builder CTAs also add items.
  const builderCheckout = 'button.ts-bb__cta.is-addon-only, button.ts-bb__foreign-note-link';
  const resumeButton = (button) => {
    if (!button.isConnected || button.disabled || !button.matches(builderCheckout)) { show('review_required', true); return; }
    replayButton = button;
    try { button.click(); }
    finally { replayButton = null; }
  };
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form === replayForm) return;
    const submitter = event.submitter;
    if (checkoutPath(submitter?.getAttribute('formaction') || form.action) ||
      (cartForm(form) && (submitter?.name === 'checkout' || (!submitter && form.querySelector('[name="checkout"]'))))) {
      intercept(event, () => resumeForm(form, submitter));
    }
  }, true);
  document.addEventListener('click', (event) => {
    const target = event.target?.closest ? event.target : event.target?.parentElement;
    if (!target || target.closest('[data-hw-buy]')) return;
    if (replayButton && target.closest('button') === replayButton) return;
    const foreignRemove = target.closest('button.ts-bb__foreign-line-remove');
    if (foreignRemove) { watchForeignRemoval(foreignRemove); return; }
    const builderButton = target.closest(builderCheckout);
    if (builderButton) { intercept(event, () => resumeButton(builderButton)); return; }
    const anchor = target.closest('a[href]');
    if (anchor && checkoutPath(anchor.href)) { intercept(event, () => window.location.assign(anchor.href)); return; }
    const button = target.closest('button[name="checkout"], input[name="checkout"]');
    if (button?.form && cartForm(button.form)) { intercept(event, () => resumeForm(button.form, button)); return; }
    if (target.closest('[data-adopt-cart-lines], [data-page-qty-change], [data-tnm-cart-drawer], form[action*="/cart"]')) recheck();
  }, true);
  document.addEventListener('change', (event) => {
    if (!event.target?.closest?.('[data-hw-campaign]') && event.target?.closest?.('form[action*="/cart"]')) recheck();
  });
  window.addEventListener('tnm:native-cart-updated', recheck);
  document.addEventListener('cart:update', recheck);
  document.addEventListener('discount:update', recheck);
  document.addEventListener('cart:updated', recheck);
  document.addEventListener('cart:change', recheck);
  document.addEventListener('torosachi:cart-updated', recheck);
  document.addEventListener('torosachi:cart-safety', (event) => {
    if (typeof event.detail?.waitUntil !== 'function') return;
    const seed = event.detail.cart && typeof event.detail.cart === 'object' ? event.detail.cart : null;
    // A builder mutation must not reuse a cart read started before its write.
    event.detail.waitUntil(ensureForHandshake(seed));
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) recheck(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) recheck(); });
  recheck();
})();
